/**
 * Onboarding-wizard lifecycle tracker (ENG-4305, plan ONB) — the SINGLE emitter
 * for the first-launch wizard's funnel.
 *
 * The wizard used to capture through `usePostHog().capture` at five screen call
 * sites, a second path that skipped `track()` and therefore skipped event-name
 * validation, the context envelope and the provider fan-out. Everything the
 * wizard reports now goes through the shared `AnalyticsTrackFn` here, and the
 * legacy name is emitted as a second leg of the SAME call rather than from a
 * second framework — so a behaviour is captured once, by one owner, under both
 * names during the parity window.
 *
 * Per rule: reference-rules-in-comments (ai.knowledge/global). Dual-write shape
 * mirrors domains/permissions-lifecycle.ts (ENG-4307); the legacy pairing is
 * read from ONBOARDING_EVENT_OWNERSHIP, never hand-written per method.
 */

import {
  EVENT_CATEGORIES,
  ONBOARDING_ADD_METHOD,
  ONBOARDING_CANONICAL_EVENT_NAMES,
  ONBOARDING_ERROR_CATEGORY,
  ONBOARDING_ERROR_SEVERITY,
  ONBOARDING_EXCEPTION_SOURCE,
  ONBOARDING_PROPERTIES,
  resolveLegacyOnboardingEventName,
  type OnboardingAddMethod,
  type OnboardingCanonicalEventName,
  type OnboardingErrorCategory,
  type OnboardingErrorSeverity,
  type OnboardingStage,
  type OnboardingStep
} from '#~/analytics/constants/index.js';
import { COMMON_PROPERTIES, DEVICE_PROPERTIES } from '#~/analytics/constants/event-properties.js';
import type { EventProperties } from '#~/analytics/types/properties.js';
import type { AnalyticsTrackFn } from './types.js';

/** Where in the wizard the event happened. Both keys ride every emission. */
export interface OnboardingContext {
  readonly stage: OnboardingStage;
  readonly step: OnboardingStep;
}

/**
 * Per-call migration switch. Defaults to dual-emitting the legacy name; pass
 * false once the parity window in canonical-event-map.json closes. Split out of
 * the envelope because it controls the dual-write, it is not a PostHog property.
 */
export interface OnboardingEmitOptions {
  readonly dualWriteLegacy?: boolean;
}

const withContext = (
  context: OnboardingContext,
  properties: EventProperties = {}
): EventProperties => ({
  ...properties,
  [ONBOARDING_PROPERTIES.STAGE]: context.stage,
  [ONBOARDING_PROPERTIES.STEP]: context.step
});

/**
 * Category -> severity + recoverability. This is the classification a PM filters
 * on: whether a failure killed the journey or is friction the user can clear.
 */
interface SeverityGrade {
  readonly severity: OnboardingErrorSeverity;
  readonly recoverable: boolean;
}

/**
 * Fallback for an uncategorized failure. A concrete const rather than a map
 * lookup so it is guaranteed defined under noUncheckedIndexedAccess, and an
 * unknown failure is graded a real error — never silently downgraded to a warning.
 */
const UNKNOWN_SEVERITY: SeverityGrade = {
  severity: ONBOARDING_ERROR_SEVERITY.ERROR,
  recoverable: true
};

const SEVERITY_BY_CATEGORY: Record<OnboardingErrorCategory, SeverityGrade> = {
  // The bridge never coming up blocks the ENTIRE desktop funnel — no camera can
  // be added without it — so it is the fatal signal, not retryable friction.
  [ONBOARDING_ERROR_CATEGORY.BRIDGE_NEVER_CONNECTED]: {
    severity: ONBOARDING_ERROR_SEVERITY.FATAL,
    recoverable: false
  },
  // Bridge still coming up when the add was attempted: transient, retryable.
  [ONBOARDING_ERROR_CATEGORY.BRIDGE_NOT_READY]: {
    severity: ONBOARDING_ERROR_SEVERITY.WARNING,
    recoverable: true
  },
  [ONBOARDING_ERROR_CATEGORY.TIMEOUT]: {
    severity: ONBOARDING_ERROR_SEVERITY.ERROR,
    recoverable: true
  },
  [ONBOARDING_ERROR_CATEGORY.UNREACHABLE]: {
    severity: ONBOARDING_ERROR_SEVERITY.ERROR,
    recoverable: true
  },
  // Just needs credentials or a plan change — friction, not a defect.
  [ONBOARDING_ERROR_CATEGORY.AUTH_REQUIRED]: {
    severity: ONBOARDING_ERROR_SEVERITY.WARNING,
    recoverable: true
  },
  [ONBOARDING_ERROR_CATEGORY.PLAN_LIMIT]: {
    severity: ONBOARDING_ERROR_SEVERITY.WARNING,
    recoverable: true
  },
  [ONBOARDING_ERROR_CATEGORY.UNKNOWN]: UNKNOWN_SEVERITY
};

/**
 * Category -> a short, PII-free description. THIS is what ships, never the raw
 * error: a raw add-device failure can embed a host, URL or credential, and no
 * onboarding payload may carry connection details.
 */
const MESSAGE_BY_CATEGORY: Record<OnboardingErrorCategory, string> = {
  [ONBOARDING_ERROR_CATEGORY.BRIDGE_NEVER_CONNECTED]:
    'Bridge did not connect within the readiness window.',
  [ONBOARDING_ERROR_CATEGORY.BRIDGE_NOT_READY]:
    'Bridge was not ready when the camera add was attempted.',
  [ONBOARDING_ERROR_CATEGORY.TIMEOUT]: 'Camera did not connect before the add timeout.',
  [ONBOARDING_ERROR_CATEGORY.UNREACHABLE]: 'Camera was unreachable on the network.',
  [ONBOARDING_ERROR_CATEGORY.AUTH_REQUIRED]: 'Camera requires credentials.',
  [ONBOARDING_ERROR_CATEGORY.PLAN_LIMIT]: 'Plan camera limit reached.',
  [ONBOARDING_ERROR_CATEGORY.UNKNOWN]: 'Uncategorized onboarding error.'
};

/**
 * Bucket a raw failure string into a queryable category. Ordering is load
 * bearing: a plan limit is a policy block and must not be reported as one of the
 * transport failures whose strings it can otherwise resemble.
 */
export const categorizeOnboardingError = (rawError: string): OnboardingErrorCategory => {
  if (rawError.includes('bridge never connected') || rawError.includes('Bridge did not connect')) {
    return ONBOARDING_ERROR_CATEGORY.BRIDGE_NEVER_CONNECTED;
  }
  if (rawError.includes('plan limit')) return ONBOARDING_ERROR_CATEGORY.PLAN_LIMIT;
  if (rawError.includes('Bridge not ready')) return ONBOARDING_ERROR_CATEGORY.BRIDGE_NOT_READY;
  if (rawError.includes('timeout') || rawError.includes('did not connect in time')) {
    return ONBOARDING_ERROR_CATEGORY.TIMEOUT;
  }
  if (rawError.includes('auth') || rawError.includes('401')) {
    return ONBOARDING_ERROR_CATEGORY.AUTH_REQUIRED;
  }
  if (rawError.includes('ECONNREFUSED') || rawError.includes('unreachable')) {
    return ONBOARDING_ERROR_CATEGORY.UNREACHABLE;
  }
  return ONBOARDING_ERROR_CATEGORY.UNKNOWN;
};

export interface OnboardingErrorClassification {
  readonly category: OnboardingErrorCategory;
  readonly severity: OnboardingErrorSeverity;
  readonly recoverable: boolean;
}

/** Classify a raw failure into the { category, severity, recoverable } triple. */
export const classifyOnboardingError = (rawError: string): OnboardingErrorClassification => {
  const category = categorizeOnboardingError(rawError);
  const { severity, recoverable } = SEVERITY_BY_CATEGORY[category] ?? UNKNOWN_SEVERITY;
  return { category, severity, recoverable };
};

export interface OnboardingFailureInput {
  readonly context: OnboardingContext;
  /** Raw failure text. Classified into a category; never emitted verbatim. */
  readonly rawError: string;
  /**
   * True when this is the outcome the user actually lived with. The framework's
   * retry rule counts only terminal outcomes, so a transient failure the user
   * then recovers from must be reported with `terminal: false` (or not at all).
   */
  readonly terminal: boolean;
  readonly properties?: EventProperties;
}

/** PII-free description of the camera being added, for joinability by source type. */
export interface OnboardingCameraContext {
  readonly addMethod?: OnboardingAddMethod;
  readonly cameraModel?: string;
}

/**
 * Build the camera half of an onboarding envelope. `model_name` (not the
 * forbidden `model`/`device_model` spellings) per the canonical device-property
 * convention in analytics/CONVENTIONS.md; the user's free-text camera name,
 * host and URL are never included.
 */
export const buildOnboardingCameraProperties = (
  camera: OnboardingCameraContext = {}
): EventProperties => ({
  ...(camera.addMethod !== undefined
    ? { [ONBOARDING_PROPERTIES.ADD_METHOD]: camera.addMethod }
    : {}),
  ...(camera.cameraModel !== undefined && camera.cameraModel !== ''
    ? { [DEVICE_PROPERTIES.MODEL_NAME]: camera.cameraModel }
    : {})
});

export const createOnboardingLifecycleTracker = (track: AnalyticsTrackFn) => {
  /**
   * Emit one behaviour: the canonical name, then its legacy twin when the parity
   * window is still open. `resolveLegacyOnboardingEventName` returns undefined
   * when there is no twin (or when the canonical name IS the legacy name), so a
   * single behaviour can never be counted twice under the same name.
   */
  const emit = (
    canonical: OnboardingCanonicalEventName,
    step: OnboardingStep | undefined,
    envelope: EventProperties,
    options?: OnboardingEmitOptions
  ): void => {
    track(canonical, EVENT_CATEGORIES.ONBOARDING, envelope);

    const legacy = resolveLegacyOnboardingEventName(canonical, step);
    if (options?.dualWriteLegacy === false || legacy === undefined) return;
    // Identical envelope on both legs: old and new rows stay 1:1 joinable, which
    // is the parity check that gates retiring the legacy name.
    track(legacy, EVENT_CATEGORIES.ONBOARDING, envelope);
  };

  return {
    /** The user entered the wizard. Fires once per first-launch funnel. */
    trackStarted: (
      context: OnboardingContext,
      properties?: EventProperties,
      options?: OnboardingEmitOptions
    ): void => {
      emit(
        ONBOARDING_CANONICAL_EVENT_NAMES.STARTED,
        undefined,
        withContext(context, properties),
        options
      );
    },

    /** A wizard screen was reached. One name + the step dimension, not one name per screen. */
    trackStepViewed: (
      context: OnboardingContext,
      properties?: EventProperties,
      options?: OnboardingEmitOptions
    ): void => {
      emit(
        ONBOARDING_CANONICAL_EVENT_NAMES.STEP_VIEWED,
        context.step,
        withContext(context, properties),
        options
      );
    },

    /** A step began work the user waits on (bridge detect, discovery, connection test). */
    trackStepStarted: (
      context: OnboardingContext,
      properties?: EventProperties,
      options?: OnboardingEmitOptions
    ): void => {
      emit(
        ONBOARDING_CANONICAL_EVENT_NAMES.STEP_STARTED,
        context.step,
        withContext(context, properties),
        options
      );
    },

    /** The user committed a choice or a form on this step. */
    trackStepSubmitted: (
      context: OnboardingContext,
      properties?: EventProperties,
      options?: OnboardingEmitOptions
    ): void => {
      emit(
        ONBOARDING_CANONICAL_EVENT_NAMES.STEP_SUBMITTED,
        context.step,
        withContext(context, properties),
        options
      );
    },

    /**
     * A stage that already reported a terminal failure came good on its own.
     * Consumers net the earlier onboarding_failed out with this, so a slow
     * bridge that eventually connects is not counted as a drop-off.
     */
    trackRecovered: (
      context: OnboardingContext,
      properties?: EventProperties,
      options?: OnboardingEmitOptions
    ): void => {
      emit(
        ONBOARDING_CANONICAL_EVENT_NAMES.RECOVERED,
        undefined,
        withContext(context, properties),
        options
      );
    },

    /** The user dismissed the wizard. Canonical name already matched, so no twin. */
    trackSkipped: (
      context: OnboardingContext,
      properties?: EventProperties,
      options?: OnboardingEmitOptions
    ): void => {
      emit(
        ONBOARDING_CANONICAL_EVENT_NAMES.SKIPPED,
        undefined,
        withContext(context, properties),
        options
      );
    },

    /** The wizard finished. The activation terminal of the onboarding funnel. */
    trackSucceeded: (
      context: OnboardingContext,
      properties?: EventProperties,
      options?: OnboardingEmitOptions
    ): void => {
      emit(
        ONBOARDING_CANONICAL_EVENT_NAMES.SUCCEEDED,
        undefined,
        withContext(context, properties),
        options
      );
    },

    /**
     * A wizard failure, on the error channel. Carries `$exception_level` so fatal
     * is filterable from noise in the same field PostHog's exception UI uses,
     * plus the error-cohort keys (`is_error_state` + `error_type`) that let error
     * volume be read against success volume.
     */
    trackFailed: (input: OnboardingFailureInput, options?: OnboardingEmitOptions): void => {
      const { category, severity, recoverable } = classifyOnboardingError(input.rawError);
      const envelope = withContext(input.context, {
        ...input.properties,
        [DEVICE_PROPERTIES.IS_ERROR_STATE]: true,
        [COMMON_PROPERTIES.ERROR_TYPE]: category,
        [COMMON_PROPERTIES.ERROR_MESSAGE]: MESSAGE_BY_CATEGORY[category],
        [ONBOARDING_PROPERTIES.EXCEPTION_LEVEL]: severity,
        [ONBOARDING_PROPERTIES.EXCEPTION_SOURCE]: ONBOARDING_EXCEPTION_SOURCE,
        [ONBOARDING_PROPERTIES.RECOVERABLE]: recoverable,
        [ONBOARDING_PROPERTIES.TERMINAL]: input.terminal
      });

      emit(ONBOARDING_CANONICAL_EVENT_NAMES.FAILED, undefined, envelope, options);
    }
  };
};

/** Re-exported so wizard call sites take the add-method values from one place. */
export { ONBOARDING_ADD_METHOD };
